/**
 * A new password's strength (decision 25: at least 10 characters and a zxcvbn score of 3 or more, because a breached
 * relay allows offline guessing, decision 52; part 36a, SU1, SU3). The scorer is zxcvbn-ts with its common dictionaries,
 * loaded only when the signup form first asks (its dictionaries are large; nothing on the start or the keystroke path
 * waits for them). The address is given as a word the password shouldn't lean on.
 */

/** The shortest password accepted. */
export const MIN_PASSWORD = 10;

/** The lowest zxcvbn score accepted. */
export const MIN_SCORE = 3;

/** zxcvbn's work grows with the length; past this many characters only the start is scored (it is long enough anyway). */
export const SCORED_LENGTH = 100;

/** A password's strength as the form shows it: nothing typed, too short, too easy to guess, good, or strong. */
export type Strength = 'empty' | 'short' | 'weak' | 'good' | 'strong';

/** zxcvbn's score (0–4) of `password`, `inputs` being words the writer's other details give. */
export type Scorer = (password: string, inputs: readonly string[]) => number;

/** SU1, SU3: the strength of `password` under `score` (null while the scorer loads: length alone then). */
export function strengthOf(password: string, score: Scorer | null, email: string): Strength {
  if (password === '') return 'empty';
  // Characters as code points, so a letter outside the BMP counts once.
  if (Array.from(password).length < MIN_PASSWORD) return 'short';
  if (score === null) return 'weak';
  const value = score(password.slice(0, SCORED_LENGTH), email.split(/[@.+_-]/u).filter((part) => part !== ''));
  if (value < MIN_SCORE) return 'weak';
  return value > MIN_SCORE ? 'strong' : 'good';
}

/** Whether a password of `strength` may make an account (SU3). */
export function acceptable(strength: Strength): boolean {
  return strength === 'good' || strength === 'strong';
}

/** Loads zxcvbn-ts with the common dictionaries and the keyboard graphs, as a {@link Scorer}. */
export async function loadScorer(): Promise<Scorer> {
  const [{ ZxcvbnFactory }, common] = await Promise.all([import('@zxcvbn-ts/core'), import('@zxcvbn-ts/language-common')]);
  const factory = new ZxcvbnFactory({ dictionary: { ...common.dictionary }, graphs: common.adjacencyGraphs });
  return (password, inputs) => factory.check(password, [...inputs]).score;
}
